/* eslint-disable max-classes-per-file */

import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import {
  MultiSelectFieldEvent,
  SingleSelectFieldEvent,
} from '@rover/shared/js/eventstream/FormFieldAnalytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

type GROUP = 'dates' | 'pets' | 'advanced';

abstract class SearchFiltersDesktopEvent extends AnalyticsEvent {
  abstract eventName: string;

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.recommendations.browse_search;
}

class SearchTopFiltersGroupOpen extends SearchFiltersDesktopEvent {
  eventName = 'desktop-filter-group-open';

  constructor(eventProperties: { group: GROUP }) {
    super(eventProperties, { group: eventProperties.group });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person opens the dates filter group in the Desktop filters top bar
 *
 * &nbsp;
 */
export class SearchTopFiltersDatesGroupOpen extends SearchTopFiltersGroupOpen {
  constructor() {
    super({ group: 'dates' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person opens the pets filter group in the Desktop filters top bar
 *
 * &nbsp;
 */
export class SearchTopFiltersPetsGroupOpen extends SearchTopFiltersGroupOpen {
  constructor() {
    super({ group: 'pets' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person opens the advanced (boolean) filter group in the Desktop filters top bar
 *
 * &nbsp;
 */
export class SearchTopFiltersAdvancedGroupOpen extends SearchTopFiltersGroupOpen {
  constructor() {
    super({ group: 'advanced' });
  }
}

class SearchTopFiltersGroupSave extends SearchFiltersDesktopEvent {
  eventName = 'desktop-filter-group-save';

  constructor(eventProperties: { group: GROUP }) {
    super(eventProperties, { group: eventProperties.group });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person saves the dates filter group in the Desktop filters top bar
 *
 * &nbsp;
 */
export class SearchTopFiltersDatesGroupSave extends SearchTopFiltersGroupSave {
  constructor() {
    super({ group: 'dates' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person saves the pets filter group in the Desktop filters top bar
 *
 * &nbsp;
 */
export class SearchTopFiltersPetsGroupSave extends SearchTopFiltersGroupSave {
  constructor() {
    super({ group: 'pets' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person saves the advanced (boolean) filter group in the Desktop filters top bar
 *
 * &nbsp;
 */
export class SearchTopFiltersAdvancedGroupSave extends SearchTopFiltersGroupSave {
  constructor() {
    super({ group: 'advanced' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person changes pet type counts on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopUpdatePetType extends MultiSelectFieldEvent {
  concept = CONCEPTS.recommendations.browse_search;

  eventName = 'search-desktop-update-pet-type';

  eventProperties: { dog: boolean; cat: boolean; puppy: boolean };

  constructor(eventProperties: { dog: boolean; cat: boolean; puppy: boolean }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person changes service type on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopUpdateServiceType extends SingleSelectFieldEvent {
  concept = CONCEPTS.recommendations.browse_search;

  eventName = 'search-desktop-update-service-type';

  eventProperties: { serviceType: string };

  constructor(eventProperties: { serviceType: string }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person updates location on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopUpdateLocation extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-update-location';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person selects dates on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopSelectDates extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-select-dates';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person selects a specific pet from their pet list on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopSelectPet extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-select-pet';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person deselects a pet from their pet list on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopDeselectPet extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-deselect-pet';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person changes dog size selection on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopUpdateSize extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-update-size';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person changes price range on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopUpdatePrice extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-update-price';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: { cardTreatment: SearchCardTreatment }) {
    super(eventProperties, { cardTreatment: eventProperties.cardTreatment });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person toggles star sitter filter on desktop search filters
 *
 * &nbsp;
 */
export class SearchDesktopToggleStarSitter extends SearchFiltersDesktopEvent {
  eventName = 'search-desktop-toggle-star-sitter';

  eventProperties: { enabled: boolean };

  constructor(eventProperties: { enabled: boolean }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person toggles a boolean filter on search filters
 *
 * &nbsp;
 */
export class SearchToggleBooleanFilter extends SearchFiltersDesktopEvent {
  eventName = 'search-toggle-boolean-filter';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person closes a filter group without saving on desktop search filters
 *
 * &nbsp;
 */
class SearchTopFiltersGroupDismiss extends SearchFiltersDesktopEvent {
  eventName = 'desktop-filter-group-dismiss';

  constructor(eventProperties: { group: GROUP }) {
    super(eventProperties, { group: eventProperties.group });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person closes the dates filter group without saving
 *
 * &nbsp;
 */
export class SearchTopFiltersDatesGroupDismiss extends SearchTopFiltersGroupDismiss {
  constructor() {
    super({ group: 'dates' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person closes the pets filter group without saving
 *
 * &nbsp;
 */
export class SearchTopFiltersPetsGroupDismiss extends SearchTopFiltersGroupDismiss {
  constructor() {
    super({ group: 'pets' });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a person closes the advanced filter group without saving
 *
 * &nbsp;
 */
export class SearchTopFiltersAdvancedGroupDismiss extends SearchTopFiltersGroupDismiss {
  constructor() {
    super({ group: 'advanced' });
  }
}
